import { useEffect, useMemo, useState } from 'react'
import { useDirectSendStatus, useSendDirect } from '@/api/hooks/useChat'
import { useContacts } from '@/api/hooks/useContacts'
import { useAccounts } from '@/api/hooks/useAccounts'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from 'sonner'
import { AlertTriangle, CheckCircle, CheckSquare, Clock, Link, Loader2, Search, Send, Users, X, XCircle } from 'lucide-react'
import { useLanguage } from '@/i18n/LanguageContext'
import type { Account, Contact } from '@/types'
import { PermissionBadge } from '@/components/shared/PermissionBadge'

const ALL_PAGES = '__all_pages__'

type SendResult = {
  pageId: string
  pageName: string
  targetCount: number
  sent: number
  failed: number
  errors: { psid: string; error: string; trace?: string }[]
  message: string
  sentAt: number
}

function toCount(value: unknown) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function sourceLabel(source?: string) {
  if (source === 'fb_dm') return 'DM'
  if (source === 'fb_comment') return 'FB'
  if (source === 'ig_comment') return 'IG'
  return 'OUT'
}

function contactSelectionKey(contact: Pick<Contact, 'pageId' | 'psid'>) {
  return `${contact.pageId}:${contact.psid}`
}

export function Chat() {
  const { t, language } = useLanguage()
  const { data: contacts, isLoading: contactsLoading } = useContacts()
  const { data: accounts, isLoading: accountsLoading } = useAccounts()
  const sendDirect = useSendDirect()

  const [pageId, setPageId] = useState('')
  const [message, setMessage] = useState('')
  const [buttonText, setButtonText] = useState('')
  const [buttonUrl, setButtonUrl] = useState('')
  const [search, setSearch] = useState('')
  const [sourceFilter, setSourceFilter] = useState<'all' | 'fb_dm' | 'fb_comment' | 'ig_comment'>('all')
  const [selectedContactKeys, setSelectedContactKeys] = useState<Set<string>>(new Set())
  const [lastResult, setLastResult] = useState<SendResult | null>(null)
  const [activeJobId, setActiveJobId] = useState<string | null>(null)

  const contactList = (contacts ?? []) as (Contact & { key: string })[]
  const accountList = (accounts ?? []) as Account[]
  const directStatus = useDirectSendStatus(activeJobId)
  const activeJob = directStatus.data
  const isJobRunning = activeJob?.status === 'queued' || activeJob?.status === 'running'

  const pageOptions = useMemo(
    () => accountList.filter((a) => a.pageId).sort((a, b) => String(a.name || a.username).localeCompare(String(b.name || b.username))),
    [accountList],
  )

  const currentPage = useMemo(
    () => pageOptions.find(a => a.pageId === pageId) || null,
    [pageOptions, pageId],
  )

  const summaryTarget = sendDirect.isPending || isJobRunning
    ? toCount(activeJob?.totalContacts ?? selectedContactKeys.size)
    : toCount(lastResult?.targetCount)

  const summarySent = toCount(activeJob?.sent ?? lastResult?.sent)
  const summaryFailed = toCount(activeJob?.failed ?? lastResult?.failed)

  const pageContacts = useMemo(() => {
    if (!pageId) return []
    if (pageId === ALL_PAGES) {
      return [...contactList].sort((a, b) => (b.lastSeen || 0) - (a.lastSeen || 0))
    }
    return contactList
      .filter((c) => c.pageId === pageId)
      .sort((a, b) => (b.lastSeen || 0) - (a.lastSeen || 0))
  }, [contactList, pageId])

  const filteredContacts = useMemo(() => {
    const q = search.trim().toLowerCase()
    return pageContacts.filter((contact) => {
      if (sourceFilter !== 'all' && contact.source !== sourceFilter) return false
      if (!q) return true
      return (contact.name || '').toLowerCase().includes(q) || contact.psid.includes(q)
    })
  }, [pageContacts, search, sourceFilter])

  const stats = useMemo(() => {
    const selectedVisible = filteredContacts.filter(c => selectedContactKeys.has(contactSelectionKey(c))).length
    return {
      total: pageContacts.length,
      selected: selectedContactKeys.size,
      visible: filteredContacts.length,
      selectedVisible,
    }
  }, [pageContacts, filteredContacts, selectedContactKeys])

  const toggleContact = (contact: Pick<Contact, 'pageId' | 'psid'>) => {
    const key = contactSelectionKey(contact)
    setSelectedContactKeys(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const selectAllVisible = () => {
    setSelectedContactKeys(new Set(filteredContacts.map(contactSelectionKey)))
  }

  const clearSelection = () => {
    setSelectedContactKeys(new Set())
  }

  const handlePageChange = (value: string) => {
    setPageId(value)
    setSearch('')
    setSourceFilter('all')
    if (value === ALL_PAGES) {
      setSelectedContactKeys(new Set(contactList.map(contactSelectionKey)))
      return
    }
    setSelectedContactKeys(new Set(contactList.filter((c) => c.pageId === value).map(contactSelectionKey)))
  }

  const handleSend = async () => {
    if (!pageId || selectedContactKeys.size === 0 || !message.trim()) return

    const selectedContacts = contactList.filter((contact) => selectedContactKeys.has(contactSelectionKey(contact)))
    const grouped = selectedContacts.reduce<Record<string, string[]>>((acc, contact) => {
      if (!acc[contact.pageId]) acc[contact.pageId] = []
      acc[contact.pageId].push(contact.psid)
      return acc
    }, {})

    try {
      const data = await sendDirect.mutateAsync({
        pageGroups: Object.entries(grouped).map(([groupPageId, psids]) => ({ pageId: groupPageId, psids })),
        message: message.trim(),
        buttonText: buttonText.trim() || undefined,
        buttonUrl: buttonUrl.trim() || undefined,
      })
      setActiveJobId(data.jobId)
      setLastResult({
        pageId,
        pageName: pageId === ALL_PAGES ? t('Todas as paginas') : currentPage?.name || currentPage?.username || pageId,
        targetCount: selectedContacts.length,
        sent: 0,
        failed: 0,
        errors: [],
        message: message.trim(),
        sentAt: Date.now(),
      })
      toast.success(t('Campanha iniciada'))
    } catch (err: any) {
      setLastResult({
        pageId,
        pageName: pageId === ALL_PAGES ? t('Todas as paginas') : currentPage?.name || currentPage?.username || pageId,
        targetCount: selectedContactKeys.size,
        sent: 0,
        failed: selectedContactKeys.size,
        errors: [{ psid: '-', error: err?.message || t('Erro ao enviar') }],
        message: message.trim(),
        sentAt: Date.now(),
      })
      toast.error(err?.message || t('Erro ao enviar'))
    }
  }

  useEffect(() => {
    if (!activeJob) return

    setLastResult({
      pageId: activeJob.pageId,
      pageName: activeJob.pageName,
      targetCount: toCount(activeJob.totalContacts),
      sent: toCount(activeJob.sent),
      failed: toCount(activeJob.failed),
      errors: activeJob.errors || [],
      message: activeJob.message,
      sentAt: activeJob.completedAt || activeJob.updatedAt || Date.now(),
    })

    if (activeJob.status === 'done') {
      toast.success(`${toCount(activeJob.sent)} ${t('enviados')}, ${toCount(activeJob.failed)} ${t('falhas')}`)
    }

    if (activeJob.status === 'failed') {
      toast.error(activeJob.errors?.[0]?.error || t('Erro ao enviar'))
    }
  }, [activeJob, t])

  if (contactsLoading || accountsLoading) {
    return (
      <div className="space-y-4">
        <div className="grid gap-3 md:grid-cols-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}
        </div>
        <div className="grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
          <Skeleton className="h-[560px] rounded-2xl" />
          <Skeleton className="h-[560px] rounded-2xl" />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <PermissionBadge permissions={[
        { name: 'pages_messaging', endpoint: 'GET /{page-id}/conversations', description: 'Reads and manages Page conversations. Allows responding to messages from users who contacted the Page.' },
      ]} />
      <div className="rounded-2xl border border-primary/15 bg-card/60 p-4 backdrop-blur-sm">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10">
              <Send className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h1 className="text-lg font-bold">{t('Disparo Direto')}</h1>
              <p className="text-xs text-muted-foreground">
                {t('Tela focada em envio em massa, selecao rapida e acompanhamento do ultimo disparo.')}
              </p>
            </div>
          </div>
          {language === 'en' && (
            <p className="text-xs text-muted-foreground/80">
              Focused bulk send panel with filters, selection, and delivery summary.
            </p>
          )}
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <div className="rounded-2xl border border-border/40 bg-card p-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Users className="h-4 w-4" />
            <span className="text-xs font-medium">{t('Total na pagina')}</span>
          </div>
          <div className="mt-2 text-2xl font-extrabold tabular-nums">{stats.total.toLocaleString()}</div>
        </div>
        <div className="rounded-2xl border border-border/40 bg-card p-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <CheckSquare className="h-4 w-4" />
            <span className="text-xs font-medium">{t('Selecionados')}</span>
          </div>
          <div className="mt-2 text-2xl font-extrabold tabular-nums">{stats.selected.toLocaleString()}</div>
        </div>
        <div className="rounded-2xl border border-border/40 bg-card p-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Search className="h-4 w-4" />
            <span className="text-xs font-medium">{t('Visiveis no filtro')}</span>
          </div>
          <div className="mt-2 text-2xl font-extrabold tabular-nums">{stats.visible.toLocaleString()}</div>
        </div>
        <div className="rounded-2xl border border-border/40 bg-card p-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span className="text-xs font-medium">{t('Selecionados no filtro')}</span>
          </div>
          <div className="mt-2 text-2xl font-extrabold tabular-nums">{stats.selectedVisible.toLocaleString()}</div>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
        <div className="space-y-4">
          <div className="rounded-2xl border border-border/40 bg-card p-5">
            <div className="space-y-4">
              <div>
                <Label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t('Pagina')}</Label>
                <Select value={pageId} onValueChange={handlePageChange}>
                  <SelectTrigger className="h-10 rounded-xl border-border/40">
                    <SelectValue placeholder={t('Selecione a pagina')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_PAGES}>{t('Todas as paginas')}</SelectItem>
                    {pageOptions.map(account => (
                      <SelectItem key={account.pageId} value={account.pageId}>
                        {account.name || account.username}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t('Mensagem')}</Label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('Ola {{nome}}, quero te mostrar uma oferta agora.')}
                  className="min-h-[120px] rounded-xl border-border/40 text-sm"
                />
                <div className="mt-2 flex flex-wrap gap-2">
                  {[
                    '{{nome}}',
                    '{{primeiro_nome}}',
                    '{{saudacao}}',
                    '{{hora}}',
                    '{{dia}}',
                    '{{data}}',
                  ].map(variable => (
                    <button
                      key={variable}
                      type="button"
                      onClick={() => setMessage(prev => prev + variable)}
                      className="rounded-lg bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
                    >
                      {variable}
                    </button>
                  ))}
                  <span className="rounded-lg bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground">
                    *texto* = <strong>negrito</strong>
                  </span>
                </div>
              </div>

              <div>
                <Label className="mb-2 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <Link className="h-3 w-3" /> {t('CTA com link (opcional)')}
                </Label>
                <div className="grid gap-2 md:grid-cols-2">
                  <Input
                    value={buttonText}
                    onChange={(e) => setButtonText(e.target.value)}
                    placeholder={t('Texto do botao')}
                    className="h-10 rounded-xl border-border/40"
                  />
                  <Input
                    value={buttonUrl}
                    onChange={(e) => setButtonUrl(e.target.value)}
                    placeholder="https://..."
                    className="h-10 rounded-xl border-border/40"
                  />
                </div>
              </div>

              <Button
                onClick={handleSend}
                disabled={sendDirect.isPending || isJobRunning || !pageId || selectedContactKeys.size === 0 || !message.trim()}
                className="h-11 w-full gap-2 rounded-xl"
              >
                {sendDirect.isPending || isJobRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {sendDirect.isPending || isJobRunning
                  ? t('Enviando...')
                  : `${t('Enviar para')} ${selectedContactKeys.size} ${t('contato(s)')}`}
              </Button>
            </div>
          </div>

          <div className="rounded-2xl border border-border/40 bg-card p-5">
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
              <div>
                <h2 className="text-sm font-bold">{t('Contatos da campanha')}</h2>
                <p className="text-xs text-muted-foreground">
                  {pageId === ALL_PAGES
                    ? `${t('Todas as paginas')} · ${stats.selected} ${t('selecionados')}`
                    : currentPage
                      ? `${currentPage.name || currentPage.username} · ${stats.selected} ${t('selecionados')}`
                      : t('Escolha uma pagina para montar a lista')}
                </p>
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" className="h-8 rounded-lg border-border/40 text-xs" onClick={selectAllVisible} disabled={!filteredContacts.length}>
                  <CheckSquare className="mr-1.5 h-3 w-3" /> {t('Selecionar filtrados')}
                </Button>
                <Button type="button" variant="ghost" className="h-8 rounded-lg text-xs" onClick={clearSelection} disabled={selectedContactKeys.size === 0}>
                  <X className="mr-1.5 h-3 w-3" /> {t('Limpar')}
                </Button>
              </div>
            </div>

            <div className="mt-4 grid gap-2 md:grid-cols-[1fr_180px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('Buscar por nome ou PSID')}
                  className="h-10 rounded-xl border-border/40 pl-9"
                />
              </div>
              <Select value={sourceFilter} onValueChange={(value: 'all' | 'fb_dm' | 'fb_comment' | 'ig_comment') => setSourceFilter(value)}>
                <SelectTrigger className="h-10 rounded-xl border-border/40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('Todas origens')}</SelectItem>
                  <SelectItem value="fb_dm">DM</SelectItem>
                  <SelectItem value="fb_comment">FB</SelectItem>
                  <SelectItem value="ig_comment">IG</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="mt-4 max-h-[460px] overflow-y-auto rounded-2xl border border-border/40">
              {!pageId ? (
                <div className="p-10 text-center text-sm text-muted-foreground">{t('Selecione uma pagina para carregar os contatos.')}</div>
              ) : filteredContacts.length === 0 ? (
                <div className="p-10 text-center text-sm text-muted-foreground">{t('Nenhum contato encontrado')}</div>
              ) : (
                filteredContacts.map(contact => (
                  <label
                    key={contact.key || contactSelectionKey(contact)}
                    className="flex cursor-pointer items-center gap-3 border-b border-border/30 px-4 py-3 transition-colors hover:bg-muted/20 last:border-b-0"
                  >
                    <Checkbox checked={selectedContactKeys.has(contactSelectionKey(contact))} onCheckedChange={() => toggleContact(contact)} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{contact.name || t('Desconhecido')}</div>
                      <div className="truncate text-[11px] text-muted-foreground">{contact.psid}</div>
                      {pageId === ALL_PAGES && (
                        <div className="truncate text-[11px] text-muted-foreground">
                          {pageOptions.find((account) => account.pageId === contact.pageId)?.name || contact.pageId}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="rounded-md border-border/40 text-[10px]">
                        {sourceLabel(contact.source)}
                      </Badge>
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl border border-border/40 bg-card p-5">
            <h2 className="text-sm font-bold">{t('Resumo do envio')}</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              {t('Painel rapido para acompanhar ultimo disparo desta tela.')}
            </p>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-xl bg-primary/5 p-3">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{t('Alvo')}</div>
                <div className="mt-1 text-xl font-extrabold tabular-nums">
                  {summaryTarget.toLocaleString()}
                </div>
              </div>
              <div className="rounded-xl bg-emerald-500/5 p-3">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{t('Enviados')}</div>
                <div className="mt-1 text-xl font-extrabold tabular-nums text-emerald-500">
                  {summarySent.toLocaleString()}
                </div>
              </div>
              <div className="rounded-xl bg-amber-500/5 p-3">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{t('Falhas')}</div>
                <div className="mt-1 text-xl font-extrabold tabular-nums text-amber-500">
                  {summaryFailed.toLocaleString()}
                </div>
              </div>
              <div className="rounded-xl bg-muted/40 p-3">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{t('Pagina')}</div>
                <div className="mt-1 truncate text-sm font-semibold">
                  {sendDirect.isPending || isJobRunning
                    ? (activeJob?.pageName || (pageId === ALL_PAGES ? t('Todas as paginas') : currentPage?.name || currentPage?.username || '-'))
                    : (lastResult?.pageName || '-')}
                </div>
              </div>
            </div>

            <div className="mt-4 rounded-xl border border-border/40 p-3">
              {sendDirect.isPending || isJobRunning ? (
                <div className="flex items-center gap-2 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  <span>
                    {activeJob
                      ? `${t('Enviando campanha...')} ${activeJob.processed}/${activeJob.totalContacts} · wave ${activeJob.currentWave}/${activeJob.totalWaves}`
                      : t('Enviando campanha...')}
                  </span>
                </div>
              ) : lastResult ? (
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    {lastResult.failed > 0 ? (
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                    ) : (
                      <CheckCircle className="h-4 w-4 text-emerald-500" />
                    )}
                    <span className="font-medium">
                      {new Date(lastResult.sentAt).toLocaleString('pt-BR')}
                    </span>
                  </div>
                  <p className="line-clamp-3 text-muted-foreground">{lastResult.message}</p>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  <span>{t('Nenhum envio executado nesta sessao.')}</span>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-border/40 bg-card p-5">
            <h2 className="text-sm font-bold">{t('Falhas e retornos')}</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              {t('Lista dos PSIDs que nao aceitaram o disparo atual e o motivo retornado.')}
            </p>

            <div className="mt-4 max-h-[420px] overflow-y-auto rounded-2xl border border-border/40">
              {!lastResult || lastResult.errors.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">
                  {lastResult && lastResult.failed === 0 ? t('Sem falhas no ultimo envio.') : t('Nenhuma falha registrada ainda.')}
                </div>
              ) : (
                lastResult.errors.map((error, index) => (
                  <div key={`${error.psid}-${index}`} className="flex items-start gap-3 border-b border-border/30 px-4 py-3 last:border-b-0">
                    <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold">{error.psid}</div>
                      <div className="mt-1 text-xs text-muted-foreground">{error.error}</div>
                      {error.trace && (
                        <div className="mt-1 break-words text-[11px] text-muted-foreground/80">
                          {error.trace}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
